import {
  AddressInV2,
  AutoRenewalCancelOut,
  CheckoutApi,
  CheckoutPurchaseInV2,
  CheckoutPurchaseInV3,
  CheckoutSupplementaryPurchaseInV2,
  Configuration,
  CustomerApi,
  CustomerDetails,
  CustomerDetailsWithOffer,
  DiscountDataV2,
  ListCustomerSubscriptionsOut,
  MembershipAutoRenewalSelfCancellationIn,
  MembershipOut,
  PaymentDetailsV2,
  PaymentDetailsV3,
  PaymentErrorDetailsV2,
  Price,
  PriceApi,
  ProductType,
  PurchaseItemV2,
  SelfCancellationUrl,
  SelfServeApi,
  SubscriptionCancelIn,
  SubscriptionCancelOut,
  SubscriptionPauseIn,
  SubscriptionPauseOut,
  SubscriptionPortalApi,
  SubscriptionUnpauseIn,
  SubscriptionUnpauseOut,
  SupplementaryOrderPaymentDetailsV2,
  TaxData,
} from '../clients/stripe-gateway'
import { getBaseConfig } from './api/rest/config'
import { CheckoutCountry } from './checkout/types'
import { User } from 'firebase/auth'

const getConfig = () => new Configuration({ baseOptions: getBaseConfig() })

const getStripeGateway = (): CheckoutApi => new CheckoutApi(getConfig(), process.env.NEXT_PUBLIC_STRIPE_GATEWAY_BASE)

const getStripePrices = (): PriceApi => new PriceApi(getConfig(), process.env.NEXT_PUBLIC_STRIPE_GATEWAY_BASE)

const getSelfServe = (): SelfServeApi => new SelfServeApi(getConfig(), process.env.NEXT_PUBLIC_STRIPE_GATEWAY_BASE)

const getCustomerApi = (): CustomerApi => new CustomerApi(getConfig(), process.env.NEXT_PUBLIC_STRIPE_GATEWAY_BASE)

const getSubscriptionPortalApi = (): SubscriptionPortalApi =>
  new SubscriptionPortalApi(getConfig(), process.env.NEXT_PUBLIC_STRIPE_GATEWAY_BASE)

export const createAndFinalizeOrder = async (
  purchase: CheckoutPurchaseInV2,
  user: User | null,
): Promise<PaymentDetailsV2> => {
  const stripeGateway = getStripeGateway()
  const headers = user ? { Authorization: `Bearer ${await user.getIdToken()}` } : {}
  const paymentDetails = await stripeGateway.createAndFinalizeOrder(purchase, { headers })
  return paymentDetails.data
}

export const cancelOrder = async (orderNumber: string, paymentErrorDetails?: PaymentErrorDetailsV2) => {
  const stripeGateway = getStripeGateway()
  await stripeGateway.cancelOrder(orderNumber, paymentErrorDetails)
}

export const getDiscountData = async (
  promotionCode: string,
  stripeCountry: string,
  onlyApplyToProducts?: string[],
): Promise<DiscountDataV2> => {
  const stripeGateway = getStripeGateway()
  const discounts = await stripeGateway.getDiscountData(promotionCode, stripeCountry, onlyApplyToProducts)
  return discounts.data
}

export const getTaxRateByAddress = async (
  stripeCountry: string,
  address: AddressInV2,
  amount: number,
  zoeProductId: string,
): Promise<TaxData> => {
  const stripeGateway = getStripeGateway()
  const taxRates = await stripeGateway.getTaxRateByAddress(stripeCountry, amount, zoeProductId, address)
  return taxRates.data
}

export const getPricesFromStripe = async (countryCode: CheckoutCountry, lookupKeys?: string[]): Promise<Price[]> => {
  const priceApi = getStripePrices()
  const pricesForLookupKeys = await priceApi.getPricesByCountry(countryCode, lookupKeys)
  return pricesForLookupKeys.data
}

export const getMembershipDetails = async (orderNumber: string, authId: string): Promise<MembershipOut> => {
  const selfServeApi = getSelfServe()
  const headers = { 'X-Cancellation-Auth-Id': authId }
  const membershipDetails = await selfServeApi.getMembership(orderNumber, { headers })
  return membershipDetails.data
}
export const selfCancelMembershipAutoRenewal = async (
  orderNumber: string,
  reasons: MembershipAutoRenewalSelfCancellationIn,
  authId: string,
): Promise<AutoRenewalCancelOut> => {
  const selfServeApi = getSelfServe()
  const headers = { 'X-Cancellation-Auth-Id': authId }
  const response = await selfServeApi.selfCancelMembershipAutoRenewal(orderNumber, reasons, { headers })
  return response.data
}

export const getCustomerDetails = async (
  username: string,
  authId: string,
  experimentId: string,
): Promise<CustomerDetailsWithOffer> => {
  const stripeGateway = getStripeGateway()
  const headers = {
    'X-Offer-Auth-Id': authId,
    'X-Offer-Experiment-Id': experimentId,
  }
  const customerDetails = await stripeGateway.getCustomerDetails(username, { headers })
  return customerDetails.data
}

export const createAndFinalizeSupplementaryOrder = async (
  username: string,
  authId: string,
  experimentId: string,
  purchase_country: string,
  items: PurchaseItemV2[],
  promotion_code?: string,
): Promise<SupplementaryOrderPaymentDetailsV2> => {
  const stripeGateway = getStripeGateway()
  const headers = {
    'X-Offer-Auth-Id': authId,
    'X-Offer-Experiment-Id': experimentId,
  }
  const purchaseIn: CheckoutSupplementaryPurchaseInV2 = {
    purchase_country,
    items,
    promotion_code,
  }
  const customerDetails = await stripeGateway.createAndFinalizeSupplementaryOrder(username, purchaseIn, { headers })
  return customerDetails.data
}

export const createAndFinalizeOrderV3 = async (
  purchase: CheckoutPurchaseInV3,
  user: User | null,
): Promise<PaymentDetailsV3> => {
  const stripeGateway = getStripeGateway()
  const headers = user ? { Authorization: `Bearer ${await user.getIdToken()}` } : {}
  const paymentDetails = await stripeGateway.createAndFinalizeOrder_2(purchase, { headers })
  return paymentDetails.data
}

export const getCustomer = async (user: User): Promise<CustomerDetails> => {
  const customerApi = getCustomerApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  const customer = await customerApi.getCustomerDetails({ headers })
  return customer.data
}

export const cancelOrderV3 = async (orderNumber: string, paymentErrorDetails?: PaymentErrorDetailsV2) => {
  const stripeGateway = getStripeGateway()
  await stripeGateway.cancelOrder_1(orderNumber, paymentErrorDetails)
}

export const pauseSubscription = async (
  user: User,
  orderNumber: string,
  resumesAt: number,
): Promise<SubscriptionPauseOut> => {
  const subscriptionPortalApi = getSubscriptionPortalApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  let subscriptionPauseIn: SubscriptionPauseIn = {
    prefixed_order_number: orderNumber,
    charge_when_resumed: true,
    resumes_at: resumesAt,
  }
  const subscriptions = await subscriptionPortalApi.pauseSubscription(subscriptionPauseIn, { headers })
  return subscriptions.data
}

export const unpauseSubscription = async (user: User, orderNumber: string): Promise<SubscriptionUnpauseOut> => {
  const subscriptionPortalApi = getSubscriptionPortalApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  let subscriptionUnpauseIn: SubscriptionUnpauseIn = {
    prefixed_order_number: orderNumber,
  }
  const subscriptions = await subscriptionPortalApi.unpauseSubscription(subscriptionUnpauseIn, { headers })
  return subscriptions.data
}

export const cancelSubscription = async (user: User, orderNumber: string): Promise<SubscriptionCancelOut> => {
  const subscriptionPortalApi = getSubscriptionPortalApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  const subscriptionCancelIn: SubscriptionCancelIn = {
    prefixed_order_number: orderNumber,
  }
  const subscriptions = await subscriptionPortalApi.cancelSubscription(subscriptionCancelIn, { headers })
  return subscriptions.data
}

export const listSubscriptions = async (user: User): Promise<ListCustomerSubscriptionsOut> => {
  const subscriptionPortalApi = getSubscriptionPortalApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  const subscriptions = await subscriptionPortalApi.listCustomerSubscriptions({ headers })
  return subscriptions.data
}

export const getCancelLinkUrl = async (user: User): Promise<SelfCancellationUrl> => {
  const subscriptionPortalApi = getSubscriptionPortalApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  const cancelLink = await subscriptionPortalApi.getCancellationUrl({ headers })
  return cancelLink.data
}

export const cancelAutoRenewal = async (user: User, orderNumber: string, product_type: ProductType) => {
  const subscriptionPortalApi = getSubscriptionPortalApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  await subscriptionPortalApi.cancelAutoRenewal(
    { prefixed_order_number: orderNumber, product_type: product_type },
    { headers },
  )
}

export const getStripeCustomerPortalUrl = async (user: User): Promise<string> => {
  const subscriptionPortalApi = getSubscriptionPortalApi()
  const headers = { Authorization: `Bearer ${await user.getIdToken()}` }
  const portalUrl = await subscriptionPortalApi.getStripePortalUrl({ headers })
  return portalUrl.data.url
}
